/**
 * Sentiers officiels du parc : liste des Itinerary / Track rattachés, et rattachement
 * d'un itinéraire perso créé dans le planificateur (inchangé).
 */
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  attachOfficialTrail,
  detachOfficialTrail,
  fetchAttachableTrails,
  fetchOfficialTrails,
  renameOfficialTrail,
  type OfficialTrail,
} from '../api'
import { useOrg } from '../utils/orgContext'
import { formatDate } from '../utils/format'
import { publicPlannerEditUrl, publicPlannerPoisUrl, publicPlannerUrl, publicTrailUrl } from '../utils/links'

function formatKm(km: number | null): string {
  if (km == null || !Number.isFinite(km)) return '—'
  return `${km.toFixed(1)} km`
}

function matchesQuery(item: OfficialTrail, q: string): boolean {
  const needle = q.trim().toLowerCase()
  if (!needle) return true
  return (item.name || '').toLowerCase().includes(needle)
}

function createdOn(item: OfficialTrail): string | null {
  return item.created_at || item.updated_at
}

export function ParcoursPage() {
  const { t } = useTranslation('gestion')
  const { orgId } = useOrg()
  const queryClient = useQueryClient()
  const [q, setQ] = useState('')
  const [pending, setPending] = useState<OfficialTrail | null>(null)
  const [renaming, setRenaming] = useState<OfficialTrail | null>(null)
  const [newName, setNewName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const official = useQuery({
    queryKey: ['official-trails', orgId],
    queryFn: ({ signal }) => fetchOfficialTrails(orgId, signal),
  })
  const attachable = useQuery({
    queryKey: ['attachable-trails', orgId],
    queryFn: ({ signal }) => fetchAttachableTrails(orgId, '', signal),
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['official-trails', orgId] })
    void queryClient.invalidateQueries({ queryKey: ['attachable-trails', orgId] })
  }

  const attach = useMutation({
    mutationFn: (item: OfficialTrail) => attachOfficialTrail(orgId, item.kind, item.id),
    onSuccess: () => {
      setError(null)
      setPending(null)
      refresh()
    },
    onError: (err: Error) => setError(err.message),
  })
  const detach = useMutation({
    mutationFn: (item: OfficialTrail) => detachOfficialTrail(orgId, item.kind, item.id),
    onSuccess: () => {
      setError(null)
      refresh()
    },
    onError: (err: Error) => setError(err.message),
  })
  const rename = useMutation({
    mutationFn: (item: OfficialTrail) => renameOfficialTrail(orgId, item.kind, item.id, newName),
    onSuccess: () => {
      setError(null)
      setRenaming(null)
      refresh()
    },
    onError: (err: Error) => setError(err.message),
  })

  useEffect(() => {
    if (!pending && !renaming) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (pending && !attach.isPending) setPending(null)
      if (renaming && !rename.isPending) setRenaming(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [pending, renaming, attach.isPending, rename.isPending])

  const openRename = (item: OfficialTrail) => {
    setError(null)
    setRenaming(item)
    setNewName(item.name || '')
  }

  const trails = official.data?.results ?? []
  const mine = attachable.data?.results ?? []
  const filtered = useMemo(() => mine.filter((item) => matchesQuery(item, q)), [mine, q])

  const onDetach = (item: OfficialTrail) => {
    if (!window.confirm(t('parcours.confirmer_retirer', { name: item.name }))) return
    detach.mutate(item)
  }

  return (
    <div className="g-page">
      <div className="g-page__head">
        <div>
          <h1 className="g-page__title">{t('parcours.titre')}</h1>
          <p className="g-muted">{t('parcours.intro')}</p>
        </div>
        <a className="btn btn--primary" href={publicPlannerUrl()} target="_blank" rel="noreferrer">
          <span className="mdi mdi-plus" aria-hidden /> {t('parcours.creer_planificateur')}
        </a>
      </div>

      {error && <p className="g-alert g-alert--error">{error}</p>}
      {official.error && <p className="g-alert g-alert--error">{(official.error as Error).message}</p>}

      <section className="g-card">
        <div className="g-card__head">
          <h2 className="g-card__title">{t('parcours.rattacher_titre')}</h2>
        </div>
        <p className="g-hint">{t('parcours.rattacher_aide')}</p>
        <div className="g-toolbar">
          <label className="g-search">
            <span className="mdi mdi-magnify" aria-hidden />
            <input
              className="g-input"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('parcours.rechercher')}
              aria-label={t('parcours.rechercher')}
            />
          </label>
        </div>
        {attachable.isLoading && <p className="g-muted">{t('commun.chargement')}</p>}
        {!attachable.isLoading && mine.length === 0 && <p className="g-hint">{t('parcours.aucun_perso')}</p>}
        {!attachable.isLoading && mine.length > 0 && filtered.length === 0 && (
          <p className="g-hint">{t('parcours.aucun_resultat')}</p>
        )}
        {filtered.length > 0 && (
          <ul className="g-attach">
            {filtered.map((item) => (
              <li key={`${item.kind}-${item.id}`} className="g-attach__row">
                <div>
                  <strong>{item.name || t('parcours.sans_nom')}</strong>
                  <p className="g-muted g-attach__meta">
                    {t(`parcours.kind_${item.kind}`)}
                    {' · '}
                    {formatKm(item.distance_km)}
                    {' · '}
                    {t('parcours.n_poi', { count: item.poi_count ?? 0 })}
                    {' · '}
                    {t('parcours.cree_le', { date: formatDate(createdOn(item)) || '—' })}
                  </p>
                </div>
                <button type="button" className="btn btn--primary" onClick={() => setPending(item)}>
                  {t('parcours.rattacher')}
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {official.isLoading && <p className="g-muted">{t('commun.chargement')}</p>}
      {!official.isLoading && trails.length === 0 && (
        <div className="g-empty">
          <span className="mdi mdi-map-marker-path g-empty__icon" aria-hidden />
          <p>{t('parcours.aucun')}</p>
        </div>
      )}

      {trails.length > 0 && (
        <div className="g-table-wrap">
          <table className="g-table">
            <thead>
              <tr>
                <th>{t('parcours.colonne_nom')}</th>
                <th>{t('parcours.colonne_type')}</th>
                <th>{t('parcours.colonne_longueur')}</th>
                <th>{t('parcours.colonne_etat')}</th>
                <th>{t('parcours.colonne_cree')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {trails.map((item) => (
                <tr key={`${item.kind}-${item.id}`}>
                  <td>
                    <span className="g-table__name">{item.name || t('parcours.sans_nom')}</span>
                  </td>
                  <td>{t(`parcours.kind_${item.kind}`)}</td>
                  <td>{formatKm(item.distance_km)}</td>
                  <td>
                    {item.moderation_status === 'pending' ? (
                      <span className="g-badge g-badge--warn">{t('parcours.en_moderation')}</span>
                    ) : item.visibility === 'public' && item.moderation_status === 'approved' ? (
                      <span className="g-badge">{t('parcours.publie')}</span>
                    ) : (
                      <span className="g-badge">{t('parcours.partage')}</span>
                    )}
                    {item.relation === 'crosses' && (
                      <span className="g-badge g-badge--warn">{t('parcours.hors_parc')}</span>
                    )}
                  </td>
                  <td>{formatDate(createdOn(item))}</td>
                  <td className="g-table__actions">
                    <div className="g-actions">
                      <button type="button" className="g-link" onClick={() => openRename(item)}>
                        {t('parcours.renommer')}
                      </button>
                      {item.kind === 'itinerary' && (
                        <>
                          <a className="g-link" href={publicPlannerEditUrl(item)} target="_blank" rel="noreferrer">
                            {t('parcours.editer')}
                          </a>
                          <a className="g-link" href={publicPlannerPoisUrl(item)} target="_blank" rel="noreferrer">
                            {t('parcours.ajouter_poi')}
                          </a>
                        </>
                      )}
                      <a className="g-link" href={publicTrailUrl(item)} target="_blank" rel="noreferrer">
                        {t('commun.voir')}
                      </a>
                      <button type="button" className="g-link g-danger" onClick={() => onDetach(item)} disabled={detach.isPending}>
                        {t('parcours.retirer')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {renaming && (
        <div className="g-dialog-backdrop" onClick={() => !rename.isPending && setRenaming(null)}>
          <div
            className="g-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="parcours-rename-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="parcours-rename-title" className="g-dialog__title">
              {t('parcours.renommer_titre')}
            </h2>
            <p className="g-muted">{t('parcours.renommer_aide')}</p>
            <form
              className="g-dialog__form"
              onSubmit={(e) => {
                e.preventDefault()
                if (newName.trim()) rename.mutate(renaming)
              }}
            >
              <label className="g-field">
                <span className="g-field__label">{t('parcours.nouveau_nom')}</span>
                <input
                  className="g-input"
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  maxLength={renaming.kind === 'itinerary' ? 200 : 255}
                  autoFocus
                  required
                />
              </label>
              <div className="g-actions">
                <button type="button" className="btn" onClick={() => setRenaming(null)} disabled={rename.isPending}>
                  {t('commun.annuler')}
                </button>
                <button type="submit" className="btn btn--primary" disabled={rename.isPending || !newName.trim()}>
                  {rename.isPending ? t('commun.enregistrement') : t('commun.enregistrer')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pending && (
        <div className="g-dialog-backdrop" onClick={() => !attach.isPending && setPending(null)}>
          <div
            className="g-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="parcours-confirm-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="parcours-confirm-title" className="g-dialog__title">
              {t('parcours.confirmer_titre')}
            </h2>
            <p className="g-muted">{t('parcours.confirmer_intro', { name: pending.name || t('parcours.sans_nom') })}</p>
            <dl className="g-dialog__facts">
              <dt>{t('parcours.fait_titre')}</dt>
              <dd>{pending.name || t('parcours.sans_nom')}</dd>
              <dt>{t('parcours.fait_distance')}</dt>
              <dd>{formatKm(pending.distance_km)}</dd>
              <dt>{t('parcours.fait_poi')}</dt>
              <dd>{t('parcours.n_poi', { count: pending.poi_count ?? 0 })}</dd>
              <dt>{t('parcours.fait_cree')}</dt>
              <dd>{formatDate(createdOn(pending)) || '—'}</dd>
            </dl>
            <div className="g-actions">
              <button type="button" className="btn" onClick={() => setPending(null)} disabled={attach.isPending}>
                {t('commun.annuler')}
              </button>
              <button type="button" className="btn btn--primary" onClick={() => attach.mutate(pending)} disabled={attach.isPending}>
                {attach.isPending ? t('commun.enregistrement') : t('parcours.rattacher')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
